import React, { useState, useEffect } from "react";
import axios from "axios";
import { NodeViewWrapper } from "@tiptap/react";
import useAuth from '../../../../hooks/useAuth';
import { getFileName } from '../../../../helpers';


function sanitizeFileName(name) {
    return name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9._-]/g, '');
}

const ImageBlockComponent = ({ node, updateAttributes, editor, getPos }) => {
    const [items, setItems] = useState(node.attrs.images || []);
    const [pendingFiles, setPendingFiles] = useState({}); // { index: File }
    const API_URL = process.env.REACT_APP_API_URL;
    const MEDIA_URL = process.env.REACT_APP_MEDIA_URL;
    const [upload, setUpload] = useState(false);
    const [file, setFile] = useState(null);

    const { user } = useAuth();

    useEffect(() => {
        setItems(node.attrs.images || []);
    }, [node.attrs.images]);

    const addItem = () => {
        const updated = [...items, {
            label: "",
            description: "",
            url: "",
            id: null,
            saving: false
        }];
        setItems(updated);
        updateAttributes({ images: updated });
    };

    const removeItem = (index) => {
        const updated = items.filter((_, i) => i !== index);
        setItems(updated);
        updateAttributes({ images: updated });
        // Also clear any pending file for this index, and shift others
        const newPending = {};
        Object.keys(pendingFiles).forEach(key => {
            const k = parseInt(key);
            if (k < index) newPending[k] = pendingFiles[k];
            if (k > index) newPending[k - 1] = pendingFiles[k];
        });
        setPendingFiles(newPending);
    };

    const handleFileChange = (index, file) => {
        setPendingFiles(prev => ({
            ...prev,
            [index]: file
        }));
    };

    // const handleUpdateImage = async (index) => {
    //     const item = items[index];
    //     if (!item.id) return;
    //     try {
    //         const token = user.jwtToken;

    //         setItems(prev => prev.map((it, i) => 
    //             i === index ? { ...it, saving: true } : it
    //         ));

    //         await axios.post(`${API_URL}/update-image`, {
    //             _id: item.id,
    //             label: item.label,
    //             description: item.description,
    //         }, {
    //             headers: { Authorization: `Bearer ${token}` },
    //         });

    //         const updatedItems = items.map((it, i) => 
    //             i === index ? { ...it, saving: false } : it
    //         );
    //         setItems(updatedItems);
    //         updateAttributes({ images: updatedItems.map(({saving, ...rest}) => rest) });

    //     } catch (err) { 
    //         console.error(err);
    //         setItems(prev => prev.map((it, i) => 
    //             i === index ? { ...it, saving: false } : it
    //         ));
    //     }
    // };

    const handleUpload = async (index) => {
        const file = pendingFiles[index];
        const item = items[index];

        if (!file) return;

        try {
            setItems(prev =>
                prev.map((it, i) =>
                    i === index ? { ...it, saving: true } : it
                )
            );

            const fileName =
                Date.now() + "_" + file.name.replace(/\s+/g, "_");

            // 1️⃣ Get presigned URL
            const presignRes = await axios.post(
                `${API_URL}/get-presign-url`,
                { file_name: fileName },
                { headers: { Authorization: `Bearer ${user.jwtToken}` } }
            );

            // 2️⃣ Upload to S3
            await axios.put(presignRes.data, file);

            // 3️⃣ Insert in DB
            const res = await axios.post(
                `${API_URL}/insert-image`,
                {
                    file_name: fileName,
                    label: item.label,
                    description: item.description,
                },
                { headers: { Authorization: `Bearer ${user.jwtToken}` } }
            );

            const updated = items.map((it, i) =>
                i === index
                    ? {
                        ...it,
                        url: fileName,
                        id: res.data._id,
                        saving: false,
                    }
                    : it
            );

            setItems(updated);
            updateAttributes({ images: updated });

            // clear pending file
            setPendingFiles(prev => {
                const copy = { ...prev };
                delete copy[index];
                return copy;
            });

        } catch (err) {
            console.error(err);
        }
    };
    /* const getPresignUrl = async (path, callback) => {
        let fileName = getFileName(sanitizeFileName(file.name));
        let URL = process.env.REACT_APP_API_URL + "/get-presign-url";
        let payload = {
            'file_name': fileName,
            'label': imageLabel,
            'description': imageDescription
        }
        setUpload(true);
        await axios.post(URL, { 'file_name': fileName }, { headers: { Authorization: 'Bearer ' + user.jwtToken } }).then((response) => {
            if (response.status !== 200) {
                throw response;
            }
            return response;
        }).then(async result => {
            const s3PresignedUrl = result.data;
            await axios({
                method: 'put',
                url: s3PresignedUrl,
                data: file,
            }).then(async (res) => {
                let insertImageURL = process.env.REACT_APP_API_URL + "/insert-image";
                await axios.post(insertImageURL, payload, { headers: { Authorization: 'Bearer ' + user.jwtToken } }).then(async (imageRes) => {
                    let imageId = imageRes.data._id;
                    setUpload(false)
                    props.setLinkImagesList([...props.linkImagesList, imageRes.data])
                    if (fileRef) {
                        fileRef.current.value = null
                        setFile(null);
                    }
                    const url = process.env.REACT_APP_MEDIA_URL + "/" + fileName;
                    setUrl(url);
                    callback(path, imageId)
                })
            })
        });
    } */

    const handleUpdateImage = async (index) => {
        const item = items[index];
        if (!item.id) return;

        try {
            setItems(prev => prev.map((it, i) => i === index ? { ...it, saving: true } : it));

            await axios.post(
                `${API_URL}/update-image`,
                {
                    _id: item.id,
                    label: item.label,
                    description: item.description,
                },
                { headers: { Authorization: `Bearer ${user.jwtToken}` } }
            );

            const updatedItems = items.map((it, i) => i === index ? { ...it, saving: false } : it);
            setItems(updatedItems);
            updateAttributes({ images: updatedItems.map(({ saving, ...rest }) => rest) });
        } catch (err) {
            console.error(err);
            setItems(prev => prev.map((it, i) => i === index ? { ...it, saving: false } : it));
        }
    };

    return (
        <NodeViewWrapper className="image-block-view">
            <div className="image-block">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                    <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "var(--text-main)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                        Image Gallery Component
                    </h3>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        {/* <button
                            className="btn btn-secondary btn-sm fw-bold"
                            style={{ display: "flex", alignItems: "center", gap: "0.25rem", padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                            onClick={(e) => {
                                e.preventDefault();
                                const html = `<div data-type="image-block" data-images='${JSON.stringify(node.attrs.images).replace(/'/g, "&apos;")}'></div>`;
                                const clipboardData = [
                                    new ClipboardItem({
                                        "text/html": new Blob([html], { type: "text/html" }),
                                        "text/plain": new Blob([JSON.stringify(node.attrs.images, null, 2)], { type: "text/plain" })
                                    })
                                ];
                                navigator.clipboard.write(clipboardData).then(() => {
                                    alert("Image Gallery block copied to clipboard!");
                                }).catch(err => {
                                    console.error("Failed to copy block:", err);
                                    // Fallback for secure context issues if clipboard API fails
                                    const textarea = document.createElement("textarea");
                                    textarea.value = html;
                                    document.body.appendChild(textarea);
                                    textarea.select();
                                    document.execCommand("copy");
                                    document.body.removeChild(textarea);
                                    alert("Image Gallery block copied!");
                                });
                            }}
                        >
                            Copy Block
                        </button>
                        <button
                            className="btn btn-secondary btn-sm fw-bold"
                            style={{ display: "flex", alignItems: "center", gap: "0.25rem", padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                            onClick={(e) => {
                                e.preventDefault();
                                const html = `<div data-type="image-block" data-images='${JSON.stringify(node.attrs.images).replace(/'/g, "&apos;")}'></div>`;
                                const clipboardData = [
                                    new ClipboardItem({
                                        "text/html": new Blob([html], { type: "text/html" }),
                                        "text/plain": new Blob([JSON.stringify(node.attrs.images, null, 2)], { type: "text/plain" })
                                    })
                                ];
                                navigator.clipboard.write(clipboardData).then(() => {
                                    const pos = getPos();
                                    editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
                                }).catch(err => {
                                    console.error("Failed to cut block:", err);
                                    // Fallback
                                    const textarea = document.createElement("textarea");
                                    textarea.value = html;
                                    document.body.appendChild(textarea);
                                    textarea.select();
                                    document.execCommand("copy");
                                    document.body.removeChild(textarea);
                                    const pos = getPos();
                                    editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
                                });
                            }}
                        >
                            Cut Block
                        </button> */}
                        <button
                            className="btn btn-danger btn-sm fw-bold"
                            style={{ display: "flex", alignItems: "center", gap: "0.25rem", padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                            onClick={(e) => {
                                e.preventDefault();
                                const pos = getPos();
                                editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
                            }}
                        >
                            Delete Block
                        </button>
                    </div>
                </div>

                <div className="image-cards-container">
                    {items.map((item, index) => {
                        if (!item) return null;
                        const hasPendingFile = !!pendingFiles[index];
                        return (
                            <div key={index} className="image-card">
                                <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "start" }}>
                                    {/* PREVIEW SIDE */}
                                    <div style={{ flex: "0 0 160px" }}>
                                        {(item.url || item.file_name) ? (
                                            <div style={{ position: "relative" }}>
                                                <img
                                                    src={`${MEDIA_URL}/${item.url || item.file_name}`}
                                                    alt=""
                                                    style={{ width: "100%", height: "140px", objectFit: "cover", borderRadius: "var(--radius-md)", border: "1px solid var(--border-color)" }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ width: "100%", height: "140px", background: "#f8fafc", borderRadius: "var(--radius-md)", border: "1px dashed #cbd5e1", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                                                No Image
                                            </div>
                                        )}
                                    </div>

                                    {/* INPUT SIDE */}
                                    <div style={{ flex: 1, minWidth: "250px" }}>
                                        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
                                            <input
                                                type="file"
                                                className="input-modern"
                                                style={{ margin: 0, padding: "0.4rem" }}
                                                onChange={(e) => handleFileChange(index, e.target.files[0])}
                                            />
                                            {hasPendingFile && (
                                                <button className="btn btn-primary" onClick={() => handleUpload(index)}>Upload</button>
                                                // <button
                                                //     className="btn btn-green2"
                                                //     type="submit"
                                                //     onClick={() => {
                                                //         // 2️⃣ UPLOAD → GET NEW imageId
                                                //         getPresignUrl(path, (path, imageId, images) => {
                                                //             // images → your new uploaded image objects (URL, id, etc.)

                                                //             const oldName = url.split("/").pop();
                                                //             // 1️⃣ Remove old image by file_name
                                                //             props.setLinkImagesList(prev =>
                                                //                 prev.filter(item => item.file_name !== oldName)
                                                //             );
                                                //             // Update node in Slate
                                                //             Transforms.setNodes(editor, { imageId }, { at: path });

                                                //             // 3️⃣ ADD NEW IMAGES TO LIST
                                                //             if (images && images.length > 0) {
                                                //                 props.setLinkImagesList(prev => [...prev, ...images]);
                                                //             }
                                                //         });
                                                //     }}
                                                // >
                                                //     {upload ? "Uploading" : "Upload"}
                                                // </button>
                                            )}
                                        </div>

                                        <input
                                            className="input-modern"
                                            placeholder="Image Label (e.g., Liver Anatomy)"
                                            value={item.label}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const updated = items.map((it, i) =>
                                                    i === index ? { ...it, label: val } : it
                                                );
                                                setItems(updated);
                                                updateAttributes({ images: updated.map(({ saving, ...rest }) => rest) });
                                            }}
                                        />

                                        <textarea
                                            className="input-modern"
                                            placeholder="Short Description..."
                                            rows="2"
                                            style={{ resize: "none" }}
                                            value={item.description}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const updated = items.map((it, i) =>
                                                    i === index ? { ...it, description: val } : it
                                                );
                                                setItems(updated);
                                                updateAttributes({ images: updated.map(({ saving, ...rest }) => rest) });
                                            }}
                                        />

                                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                                            <button
                                                className="btn btn-ghost"
                                                onClick={() => handleUpdateImage(index)}
                                                disabled={item.saving || !item.id}
                                            >
                                                {item.saving ? "Saving..." : "Update Metadata"}
                                            </button>
                                            <button className="btn btn-danger" style={{ padding: "0.5rem" }} onClick={() => removeItem(index)}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button
                    className="btn btn-primary"
                    style={{ width: "100%", marginTop: "1rem", background: "white", color: "var(--primary)", border: "1px dashed var(--primary)" }}
                    onClick={addItem}
                >
                    + Add Image
                </button>
            </div>
        </NodeViewWrapper>
    );
};

export default ImageBlockComponent;